import { type CSSProperties, type FC, Suspense, useRef } from 'react';

import { useInView } from '@shared/lib';
import { tokens } from '@shared/styles/tokens';
import type { ICustomerLogo } from '@shared/types';
import { Picture } from '@shared/ui/Picture';

import styles from './CustomerLogos.module.css';

interface CustomerLogosProps {
  customers: readonly ICustomerLogo[];
}

interface RowProps extends CustomerLogosProps {
  reverse?: boolean;
}

const trackStyle = (count: number): CSSProperties & { '--marquee-duration': string } => ({
  '--marquee-duration': `${count * tokens.motion.marqueeItemSec}s`,
});

const LogoList: FC<CustomerLogosProps & { hidden?: boolean }> = ({ customers, hidden }) => (
  <ul className={styles.list} aria-hidden={hidden}>
    {customers.map((c) => (
      <li className={styles.item} key={c.id}>
        <div className={styles.plate} data-coat={c.kind === 'coat'}>
          {/* Подпись — название заказчика; картинка (его знак или герб его территории) — оформление, alt пустой */}
          <Picture
            className={styles.logo}
            sizes="clamp(136px, 11.5vw, 168px)"
            src={c.logo}
            width={c.width}
            height={c.height}
            alt=""
          />
        </div>
        <span className={styles.caption}>{c.name}</span>
      </li>
    ))}
  </ul>
);

// Вторая копия списка нужна для бесшовного цикла и скрыта от скринридеров. Создаётся, только
// когда секция подходит к экрану: высоту она не меняет, а на старте это ещё ~900 узлов работы.
// Без копии лента стоит — движение включается вместе с ней
// Строка фокусируется: с клавиатуры её можно прокрутить, а фокус останавливает движение
const Row: FC<RowProps & { label: string; running: boolean; looped: boolean }> = ({
  customers,
  reverse,
  label,
  running,
  looped,
}) => (
  <div className={styles.viewport} tabIndex={0} role="region" aria-label={label}>
    <div
      className={styles.track}
      style={trackStyle(customers.length)}
      data-reverse={reverse}
      data-looped={looped}
      data-running={running && looped}
    >
      <LogoList customers={customers} />
      {looped && <LogoList customers={customers} hidden />}
    </div>
  </div>
);

/**
 * Две встречные бесконечные ленты знаков заказчиков: собственные логотипы и гербы территорий.
 * Строки есть сразу (и в пререндере): раньше они появлялись при подходе к экрану, секция
 * вырастала и сдвигала страницу. Картинки грузит браузер сам по мере появления (loading="lazy")
 */
export const CustomerLogos: FC<CustomerLogosProps> = ({ customers }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Вне экрана ленты стоят: бесконечная анимация не должна работать впустую
  const visible = useInView(ref);
  // Копии для цикла — заранее, до появления на экране. До первого срабатывания наблюдателя
  // их нет — как и в пререндере, гидратация не расходится
  const looped = useInView(ref, { margin: '400px 0px', once: true });
  const top = customers.filter((_, i) => i % 2 === 0);
  const bottom = customers.filter((_, i) => i % 2 === 1);

  return (
    <div className={styles.root} ref={ref}>
      {/* Отдельная граница гидратации: полторы сотни знаков не задерживают первый экран */}
      <Suspense fallback={null}>
        <Row customers={top} label="Знаки заказчиков, строка 1" running={visible} looped={looped} />
        {bottom.length > 0 && (
          <Row
            customers={bottom}
            reverse
            label="Знаки заказчиков, строка 2"
            running={visible}
            looped={looped}
          />
        )}
      </Suspense>
    </div>
  );
};
